import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ConfidenceLabel } from "@/components/ConfidenceLabel";
import { DeliveryTicker } from "@/components/DeliveryTicker";
import { NavBar } from "@/components/NavBar";
import { AdVerifyLive } from "./AdVerifyLive";
import { AdVerifyBookings } from "./AdVerifyBookings";
import { api, API_BASE } from "@/lib/api";
import { T, LSM_BANDS } from "@/lib/theme";

const C = T.term;

const TABS = [
  { id: "overview",     label: "Campaign Overview" },
  { id: "live",         label: "Live Detections" },
  { id: "delivery",     label: "Station Delivery" },
  { id: "certificates", label: "Certificates" },
];

function NewCampaignForm({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [formError, setFormError] = useState("");
  const [form, setForm] = useState({
    name: "Q1 Drive", brand: "MTN Ghana", product: "MoMo Super Promo", ad_title: "MoMo Super Promo",
    budget_ghs: 42000, target_gender: "all",
    target_age_min: 18, target_age_max: 45,
    target_lsm_min: 5, target_lsm_max: 10,
  });
  const [saving, setSaving] = useState(false);
  const inp = { width: "100%", padding: "8px 10px", borderRadius: 6, background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontSize: 13, fontFamily: C.mono };

  const save = async () => {
    setFormError("");
    setSaving(true);
    try {
      const { data } = await api.post("/campaigns", form);
      onCreated(data);
      setOpen(false);
    } catch (e) {
      setFormError(e.response?.data?.detail || "Failed to create campaign");
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button data-testid="new-campaign-btn" onClick={() => setOpen(true)}
        style={{ width: "100%", padding: "10px", borderRadius: 8, background: C.greenDim, border: `1px solid ${C.green}44`, color: C.green, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: C.mono }}>
        + New Campaign
      </button>
    );
  }

  return (
    <div data-testid="new-campaign-form" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: 12, marginBottom: 8 }}>
      {[
        ["name", "Campaign Name"], ["brand", "Brand"], ["product", "Product"],
        ["ad_title", "Ad Title (text the crawler matches)"],
      ].map(([k, l]) => (
        <div key={k} style={{ marginBottom: 8 }}>
          <div style={{ color: C.muted, fontSize: 10, fontFamily: C.mono, marginBottom: 3 }}>{l.toUpperCase()}</div>
          <input data-testid={`form-${k}`} style={inp} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
        </div>
      ))}
      <div style={{ marginBottom: 8 }}>
        <div style={{ color: C.muted, fontSize: 10, fontFamily: C.mono, marginBottom: 3 }}>BUDGET (GH₵)</div>
        <input data-testid="form-budget" type="number" style={inp} value={form.budget_ghs} onChange={(e) => setForm({ ...form, budget_ghs: Number(e.target.value) })} />
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <button data-testid="form-cancel" onClick={() => setOpen(false)} style={{ flex: 1, padding: "8px", background: "transparent", border: `1px solid ${C.border}`, color: C.muted, borderRadius: 6, cursor: "pointer", fontSize: 12 }}>Cancel</button>
        <button data-testid="form-save" onClick={save} disabled={saving} style={{ flex: 1, padding: "8px", background: C.green, color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 700 }}>
          {saving ? "..." : "Create"}
        </button>
      </div>
      {formError && <div data-testid="form-error" style={{ color: "#EF4444", fontSize: 12, marginTop: 8 }}>{formError}</div>}
    </div>
  );
}

function Seal({ campaign, reach, deliveryPct }) {
  const [stamped, setStamped] = useState(false);
  const [count, setCount] = useState(0);

  useEffect(() => {
    setStamped(false); setCount(0);
    const t = setTimeout(() => setStamped(true), 400);
    return () => clearTimeout(t);
  }, [campaign?.id]);

  useEffect(() => {
    if (!stamped) return;
    let n = 0; const target = reach; const step = Math.max(1, Math.ceil(target / 60));
    const t = setInterval(() => { n = Math.min(n + step, target); setCount(n); if (n >= target) clearInterval(t); }, 20);
    return () => clearInterval(t);
  }, [stamped, reach]);

  const color = deliveryPct >= 90 ? C.green : deliveryPct >= 70 ? "#F59E0B" : "#F43F5E";

  return (
    <div data-testid="campaign-seal" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: "32px 36px", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: -80, right: -80, width: 300, height: 300, borderRadius: "50%", background: `radial-gradient(circle,${color}10,transparent)`, pointerEvents: "none" }} />
      <div style={{ display: "flex", gap: 40, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ width: 140, height: 140, borderRadius: "50%", flexShrink: 0, border: `3px solid ${color}`,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          boxShadow: stamped ? `0 0 0 6px ${color}18,0 0 40px ${color}30` : "none",
          background: `radial-gradient(circle at center,${color}08,transparent)`,
          transform: stamped ? "scale(1)" : "scale(0.85)", opacity: stamped ? 1 : 0,
          transition: "transform 0.5s ease,opacity 0.5s ease,box-shadow 0.6s ease" }}>
          <div style={{ fontSize: 32, color }}>{deliveryPct >= 90 ? "✓" : "●"}</div>
          <div style={{ color, fontSize: 10, fontWeight: 800, fontFamily: C.mono, letterSpacing: "0.12em", textAlign: "center", textTransform: "uppercase", lineHeight: 1.3 }}>
            {deliveryPct >= 90 ? "VERIFIED" : "IN PROGRESS"}
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <span style={{ color: C.muted, fontSize: 12, fontFamily: C.mono }}>{campaign?.id?.slice(0, 8) || ""}</span>
            <span style={{ background: color + "22", color, border: `1px solid ${color}44`, padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700, fontFamily: C.mono }}>
              {deliveryPct >= 90 ? "✓ VERIFIED" : "● ACTIVE"}
            </span>
          </div>
          <div style={{ color: C.text, fontSize: 14, fontWeight: 600, marginBottom: 20 }}>{campaign?.brand} · {campaign?.product || campaign?.name}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))", gap: "0 32px" }}>
            {[
              { label: "Verified Reach", value: count.toLocaleString(), accent: color, large: true, testid: "seal-reach" },
              { label: "Spots Verified", value: campaign?.spots_verified ?? 0, accent: C.muted, testid: "seal-spots" },
              { label: "Delivery Rate", value: `${deliveryPct}%`, accent: color, testid: "seal-delivery" },
              { label: "Budget", value: `GH₵ ${(campaign?.budget_ghs || 0).toLocaleString()}`, accent: C.text, testid: "seal-budget" },
            ].map((item, i) => (
              <div key={i} data-testid={item.testid}>
                <div style={{ color: C.muted, fontSize: 10, fontFamily: C.mono, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>{item.label}</div>
                <div style={{ color: item.accent, fontSize: item.large ? 34 : 20, fontWeight: 800, fontFamily: C.mono, lineHeight: 1, textShadow: item.large ? `0 0 30px ${color}55` : "none" }}>{item.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdVerify() {
  const nav = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("overview");
  const [pulse, setPulse] = useState(null);
  const [stations, setStations] = useState([]);
  const [certForm, setCertForm] = useState({ station_id: "", ad_title: "MoMo Super Promo 30s", client_name: "MTN Ghana", agency_name: "Origin8", duration_seconds: 30 });
  const [verifyReport, setVerifyReport] = useState(null);
  const [certMsg, setCertMsg] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [c, p, s] = await Promise.all([
        api.get("/campaigns"),
        api.get("/realtime/pulse"),
        // Use ratings endpoint — already enriched with is_online + confidence_label
        // per station, so the dropdown can flag online streams (DIRECT certs)
        // vs offline (MODEL fallback certs) before the user clicks generate.
        api.get("/ratings/country/Ghana"),
      ]);
      setCampaigns(c.data.campaigns || []);
      setPulse(p.data);
      // Reshape ranking rows to a station-picker-friendly list.
      const rows = (s.data.rankings || []).map((r) => ({
        id: r.station_id,
        name: r.name,
        frequency: r.frequency,
        is_online: r.is_online,
        confidence_label: r.confidence_label,
        data_source: r.data_source,
      }));
      // Sort: online first, then by name. Online stations produce DIRECT certs.
      rows.sort((a, b) => (b.is_online ? 1 : 0) - (a.is_online ? 1 : 0) || a.name.localeCompare(b.name));
      setStations(rows);
      if (!selected && c.data.campaigns?.[0]) setSelected(c.data.campaigns[0]);
      if (!certForm.station_id && rows[0]) {
        setCertForm((f) => ({ ...f, station_id: rows[0].id }));
      }
    } catch (e) { console.error(e); }
  }, [selected, certForm.station_id]);

  useEffect(() => { refresh(); }, []);

  useEffect(() => {
    if (!selected) { setVerifyReport(null); return; }
    api.get(`/campaigns/${selected.id}/verify`).then((r) => setVerifyReport(r.data)).catch(() => {});
  }, [selected]);

  const spotsBooked = verifyReport?.spots_booked || selected?.spots_booked || 0;
  const spotsVerified = verifyReport?.spots_verified || 0;
  const deliveryPct = spotsBooked > 0
    ? Math.min(100, Math.round((spotsVerified / spotsBooked) * 100))
    : (verifyReport?.total_verified_reach > 0 ? 100 : 0);
  const reach = verifyReport?.total_verified_reach || 0;
  const sealCampaign = selected ? { ...selected, spots_verified: verifyReport?.spots_verified || 0 } : null;

  const generateCertificate = async () => {
    setCertMsg(null);
    try {
      const body = { ...certForm, campaign_id: selected?.id };
      const { data } = await api.post("/certificates", body);
      setCertMsg({ type: "success", text: "Certificate generated successfully.", certNumber: data.certificate_number });
      setTimeout(() => setCertMsg(null), 6000);
      const r = await api.get(`/campaigns/${selected.id}/verify`);
      setVerifyReport(r.data);
    } catch (e) {
      setCertMsg({ type: "error", text: e.response?.data?.detail || "Generation failed. Please try again." });
    }
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: C.bg, fontFamily: "'Inter',sans-serif" }} data-testid="adverify-page">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Inter:wght@400;500;600;700&display=swap');*{box-sizing:border-box}body{margin:0}
        ::-webkit-scrollbar{width:4px}::-webkit-scrollbar-thumb{background:${C.border};border-radius:4px}`}</style>

      {/* Sidebar */}
      <div className="adverify-sidebar" style={{ width: 260, flexShrink: 0, background: C.surface, borderRight: `1px solid ${C.border}`, display: "flex", flexDirection: "column", position: "sticky", top: 0, height: "100vh" }}>
        <div style={{ padding: "20px", borderBottom: `1px solid ${C.border}`, cursor: "pointer" }} onClick={() => nav("/")} data-testid="adverify-logo">
          <div style={{ fontWeight: 800, fontSize: 16, color: C.text }}>WaveScore<span style={{ color: C.green }}>Pro</span></div>
          <div style={{ color: C.green, fontSize: 10, fontFamily: C.mono }}>AdVerify™</div>
        </div>

        <div style={{ padding: "14px 16px", borderBottom: `1px solid ${C.border}`, maxHeight: "50vh", overflowY: "auto" }}>
          <div style={{ color: C.muted, fontSize: 10, fontFamily: C.mono, letterSpacing: "0.08em", marginBottom: 6 }}>ACTIVE CAMPAIGNS</div>
          <NewCampaignForm onCreated={(c) => { setCampaigns((cs) => [c, ...cs]); setSelected(c); }} />
          {campaigns.map((c) => (
            <div key={c.id} data-testid={`campaign-item-${c.id}`} onClick={() => setSelected(c)}
              style={{ padding: "10px 12px", borderRadius: 8, cursor: "pointer", marginBottom: 4,
                background: selected?.id === c.id ? C.greenDim : "transparent",
                border: selected?.id === c.id ? `1px solid ${C.green}33` : "1px solid transparent" }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: selected?.id === c.id ? C.green : C.text }}>{c.brand}</div>
              <div style={{ fontSize: 11, fontFamily: C.mono, color: C.muted }}>{c.name}</div>
            </div>
          ))}
          {campaigns.length === 0 && <div style={{ color: C.muted, fontSize: 12, marginTop: 8 }}>No campaigns yet — create one above.</div>}
        </div>

        <nav style={{ flex: 1, padding: "10px" }}>
          {TABS.map((t) => (
            <button key={t.id} data-testid={`adv-tab-${t.id}`} onClick={() => setTab(t.id)}
              style={{ width: "100%", display: "flex", alignItems: "center", padding: "10px 12px", borderRadius: 8, marginBottom: 2,
                background: tab === t.id ? C.greenDim : "transparent",
                border: tab === t.id ? `1px solid ${C.green}33` : "1px solid transparent",
                color: tab === t.id ? C.green : C.muted,
                cursor: "pointer", fontSize: 12, fontWeight: tab === t.id ? 600 : 400, textAlign: "left" }}>
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Main */}
      <div style={{ flex: 1, padding: "28px 32px", overflowY: "auto" }}>
        {/* Mobile campaign selector — visible only below 768px */}
        <div className="adverify-mobile-selector"
          style={{ display: "none", padding: "12px 0 16px", marginBottom: 4, borderBottom: `1px solid ${C.border}` }}>
          <select
            value={selected?.id || ""}
            onChange={(e) => {
              const found = campaigns.find((c) => c.id === e.target.value);
              if (found) setSelected(found);
            }}
            style={{ width: "100%", padding: "10px 12px", background: C.surface,
              border: `1px solid ${C.border}`, color: C.text, borderRadius: 8,
              fontFamily: "inherit", fontSize: 13 }}>
            <option value="" disabled>Select a campaign…</option>
            {campaigns.map((c) => (
              <option key={c.id} value={c.id}>{c.brand} — {c.name}</option>
            ))}
          </select>
        </div>
        <div style={{ marginBottom: 24 }}>
          <div style={{ color: C.muted, fontSize: 12, fontFamily: C.mono, marginBottom: 6 }}>CAMPAIGN VERIFICATION CONSOLE</div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: C.text }}>Ad Campaign Verification</h1>
        </div>

        {!selected && (
          <>
            <DeliveryTicker />
            <div data-testid="empty-state" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: "60px 40px", textAlign: "center", marginTop: 20 }}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>📊</div>
            <h2 style={{ color: C.text, fontSize: 22, fontWeight: 800, marginBottom: 8 }}>Create your first campaign</h2>
            <p style={{ color: C.muted, fontSize: 14, lineHeight: 1.7, maxWidth: 480, margin: "0 auto 24px" }}>
              Track ad delivery in real time, generate independent transmission certificates, and prove reach to your clients — all from one console.
            </p>
            <ol style={{ textAlign: "left", color: C.muted, fontSize: 13, lineHeight: 1.9, maxWidth: 420, margin: "0 auto 28px", paddingLeft: 20 }}>
              <li>Click <b style={{ color: C.green }}>+ New Campaign</b> in the sidebar →</li>
              <li>Enter your brand, product and budget</li>
              <li>The crawler auto-issues a Transmission Certificate every time your ad airs</li>
              <li>Download verified PDFs or share the public verify link with your client</li>
            </ol>
            <div style={{ display: "inline-block", padding: "10px 16px", background: C.greenDim, border: `1px solid ${C.green}44`, borderRadius: 10, color: C.green, fontSize: 12, fontWeight: 600 }}>
              👈 Start in the sidebar
            </div>
          </div>
          </>
        )}

        {selected && tab === "overview" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <DeliveryTicker />
            <Seal campaign={sealCampaign} reach={reach} deliveryPct={deliveryPct} />

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 14 }}>
              {[
                { label: "Target Gender", value: selected.target_gender === "all" ? "All genders" : selected.target_gender, accent: "#3B82F6" },
                { label: "Target Age",    value: `${selected.target_age_min}–${selected.target_age_max}`, accent: C.green },
                { label: "Target LSM",    value: `LSM ${selected.target_lsm_min}–${selected.target_lsm_max}`, accent: "#F59E0B" },
                { label: "Budget",        value: `GH₵ ${(selected.budget_ghs || 0).toLocaleString()}`, accent: C.text },
              ].map((card, i) => (
                <div key={i} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "18px 20px", position: "relative", overflow: "hidden" }}>
                  <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg,${card.accent},transparent)` }} />
                  <div style={{ color: C.muted, fontSize: 10, fontFamily: C.mono, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>{card.label}</div>
                  <div style={{ color: C.text, fontSize: 22, fontWeight: 700, fontFamily: C.mono, lineHeight: 1 }}>{card.value}</div>
                </div>
              ))}
            </div>

            {pulse && (
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24 }} data-testid="market-overview">
                <div style={{ color: C.text, fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Live Market Overview — West Africa</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16 }}>
                  {[
                    { label: "Stations Tracked", value: pulse.total_tracked, color: C.green },
                    { label: "Currently Online", value: pulse.stations_online, color: C.green },
                    { label: "Total Est. People", value: (pulse.total_estimated_people || 0).toLocaleString(), color: "#F59E0B" },
                  ].map((s, i) => (
                    <div key={i} style={{ textAlign: "center" }}>
                      <div style={{ color: s.color, fontSize: 28, fontWeight: 800, fontFamily: C.mono }}>{s.value}</div>
                      <div style={{ color: C.muted, fontSize: 12 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {selected && tab === "live" && (
          <div data-testid="live-tab" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <DeliveryTicker />
            <AdVerifyBookings campaign={selected} stations={stations} data-testid="adverify-bookings-panel" />
            <AdVerifyLive data-testid="adverify-live-panel" />
          </div>
        )}

        {selected && tab === "delivery" && (
          <div data-testid="delivery-tab" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24 }}>
              <div style={{ color: C.text, fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Generate Transmission Certificate</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12, marginBottom: 16 }}>
                <div>
                  <div style={{ color: C.muted, fontSize: 11, marginBottom: 4 }}>Station</div>
                  <select data-testid="cert-station" value={certForm.station_id} onChange={(e) => setCertForm({ ...certForm, station_id: e.target.value })}
                    style={{ width: "100%", padding: "10px", borderRadius: 6, background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontFamily: C.mono, fontSize: 12 }}>
                    {stations.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.is_online ? "🟢" : "⚫"} {s.name} {s.frequency ? `· ${s.frequency}` : ""} {s.is_online ? "· LIVE" : "· offline"}
                      </option>
                    ))}
                  </select>
                  {(() => {
                    const picked = stations.find((s) => s.id === certForm.station_id);
                    if (!picked) return null;
                    const willBeDirect = picked.is_online;
                    return (
                      <div data-testid="cert-projected-confidence"
                        style={{ marginTop: 6, fontSize: 10, color: willBeDirect ? C.green : "#F59E0B", lineHeight: 1.5 }}>
                        {willBeDirect
                          ? "✅ Stream is LIVE — this certificate will be tagged DIRECT (RT+ scraped airplay)."
                          : "⚠ Stream is offline — certificate will fall back to MODEL (synthetic estimate). Pick an online station for a verified DIRECT cert."}
                      </div>
                    );
                  })()}
                </div>
                <div>
                  <div style={{ color: C.muted, fontSize: 11, marginBottom: 4 }}>Ad Title</div>
                  <input data-testid="cert-title" value={certForm.ad_title} onChange={(e) => setCertForm({ ...certForm, ad_title: e.target.value })}
                    style={{ width: "100%", padding: "10px", borderRadius: 6, background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontFamily: C.mono, fontSize: 12 }} />
                </div>
                <div>
                  <div style={{ color: C.muted, fontSize: 11, marginBottom: 4 }}>Client</div>
                  <input data-testid="cert-client" value={certForm.client_name} onChange={(e) => setCertForm({ ...certForm, client_name: e.target.value })}
                    style={{ width: "100%", padding: "10px", borderRadius: 6, background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontFamily: C.mono, fontSize: 12 }} />
                </div>
                <div>
                  <div style={{ color: C.muted, fontSize: 11, marginBottom: 4 }}>Agency</div>
                  <input data-testid="cert-agency" value={certForm.agency_name} onChange={(e) => setCertForm({ ...certForm, agency_name: e.target.value })}
                    style={{ width: "100%", padding: "10px", borderRadius: 6, background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontFamily: C.mono, fontSize: 12 }} />
                </div>
              </div>
              <button data-testid="cert-generate-btn" onClick={generateCertificate}
                style={{ padding: "10px 20px", background: C.green, color: "#fff", border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer", fontFamily: C.mono, fontSize: 13 }}>
                Generate certificate
              </button>
              {certMsg && (
                <div data-testid="cert-msg" style={{
                  marginTop: 12, padding: "10px 14px", borderRadius: 8,
                  background: certMsg.type === "success" ? C.green + "18" : "#EF444418",
                  border: `1px solid ${certMsg.type === "success" ? C.green + "55" : "#EF444455"}`,
                  color: certMsg.type === "success" ? C.green : "#EF4444",
                  fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>
                    {certMsg.type === "success" ? "✓ " : "⚠ "}{certMsg.text}
                    {certMsg.certNumber && (
                      <a href={`/verify/${certMsg.certNumber}`} target="_blank" rel="noreferrer"
                        style={{ marginLeft: 8, color: C.green, fontFamily: C.mono, fontSize: 12, fontWeight: 700 }}>
                        {certMsg.certNumber} →
                      </a>
                    )}
                  </span>
                  <button onClick={() => setCertMsg(null)}
                    style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", fontSize: 16, padding: "0 4px" }}>×</button>
                </div>
              )}
            </div>

            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24 }}>
              <div style={{ color: C.text, fontSize: 14, fontWeight: 600, marginBottom: 8 }}>About Campaign Delivery Verification</div>
              <div style={{ color: C.muted, fontSize: 13, lineHeight: 1.7 }}>
                WaveScorePro verifies ad delivery through its real-time stream crawler that monitors each station every 60 seconds.
                When an ad title is detected in the ICY stream metadata, a verification record is created with the exact timestamp,
                listener count, demographic profile, and a WaveScorePro verification seal. This is independent of the station's own log.
              </div>
              <div style={{ marginTop: 12 }}>
                <strong style={{ color: C.green }}>Data source: </strong>
                <ConfidenceLabel source="DIRECT" /> Stream server read · Independent third-party verification.
              </div>
            </div>
          </div>
        )}

        {selected && tab === "certificates" && (
          <div data-testid="certificates-tab" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24 }}>
            <div style={{ color: C.text, fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Issued Transmission Certificates</div>
            {!verifyReport || verifyReport.certificates.length === 0 ? (
              <div style={{ color: C.muted, fontSize: 13 }}>No certificates yet. Go to "Station Delivery" to issue one.</div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", color: C.text, fontSize: 12 }}>
                <thead>
                  <tr style={{ color: C.muted, textAlign: "left" }}>
                    {["Certificate #", "Station", "Ad Title", "Source", "Reach", "Issued", "PDF"].map((h) => (
                      <th key={h} style={{ padding: "8px 6px", borderBottom: `1px solid ${C.border}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {verifyReport.certificates.map((c) => (
                    <tr key={c.certificate_number} data-testid={`cert-row-${c.certificate_number}`}>
                      <td style={{ padding: "10px 6px", fontFamily: C.mono }}>{c.certificate_number}</td>
                      <td style={{ padding: "10px 6px" }}>{c.station_name}</td>
                      <td style={{ padding: "10px 6px" }}>{c.ad_title}</td>
                      <td style={{ padding: "10px 6px" }}><ConfidenceLabel source={c.confidence_label} size="xs" /></td>
                      <td style={{ padding: "10px 6px", fontFamily: C.mono }}>{(c.estimated_total_reach || 0).toLocaleString()}</td>
                      <td style={{ padding: "10px 6px", color: C.muted }}>{c.issued_at?.slice(0, 16)}</td>
                      <td style={{ padding: "10px 6px" }}>
                        <a href={`${API_BASE}/certificates/${c.certificate_number}/pdf`} target="_blank" rel="noreferrer"
                          data-testid={`cert-pdf-${c.certificate_number}`}
                          style={{ color: C.green, fontWeight: 700, textDecoration: "none" }}>↓ PDF</a>
                        <span style={{ color: C.muted, margin: "0 6px" }}>·</span>
                        <a href={`/verify/${c.certificate_number}`} target="_blank" rel="noreferrer"
                          data-testid={`cert-verify-${c.certificate_number}`}
                          style={{ color: C.green, textDecoration: "none" }}>verify</a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
